import { useState } from "react";
import { X, Upload, Loader2, CheckCircle2, AlertTriangle, FileSpreadsheet } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { fmt, opts } from "./constants";

const CONFIG = {
  bocas: {
    title: "Importar Desglose de BOCAS",
    hint: "Subí el Excel real de bocas con composición (columnas: descripción, unidad, cantidad, precio unit., precio total).",
    endpoint: "bocas",
  },
  "mano-obra": {
    title: "Importar Desglose de MANO DE OBRA",
    hint: "Subí el Excel real de mano de obra (rubros con horas de Ayudante/Oficial y equipamiento especial).",
    endpoint: "mano-obra",
  },
};

export function ImportDesgloseModal({ open, type, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  if (!open) return null;
  const cfg = CONFIG[type];

  const reset = () => { setFile(null); setPreview(null); };
  const handleClose = () => { reset(); onClose(); };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
    setPreview(null);
  };

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/bocas-precios/${cfg.endpoint}/import-desglose/preview`, fd, {
        ...opts, headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al analizar el archivo");
    } finally { setLoading(false); }
  };

  const handleConfirm = async () => {
    if (!file) return;
    setConfirming(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/bocas-precios/${cfg.endpoint}/import-desglose/confirm`, fd, {
        ...opts, headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Importado: ${data.imported} nuevos, ${data.updated} actualizados`);
      reset();
      onImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al confirmar la importación");
    } finally { setConfirming(false); }
  };

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center p-4" data-testid="import-desglose-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="text-base font-bold text-gray-900 flex items-center gap-2"><FileSpreadsheet size={16} className="text-violet-600" /> {cfg.title}</h3>
          <button onClick={handleClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={16} /></button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-xs text-gray-500">{cfg.hint} No se modifica nada hasta que confirmes.</p>

          {!preview && (
            <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
              <input type="file" accept=".xlsx,.xls" onChange={handleFileChange} className="hidden" id={`desglose-file-${type}`} data-testid="import-desglose-file-input" />
              <label htmlFor={`desglose-file-${type}`} className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer">
                <Upload size={14} /> {file ? file.name : "Elegir archivo Excel"}
              </label>
              {file && (
                <div className="mt-4">
                  <button onClick={handlePreview} disabled={loading}
                    className="inline-flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
                    data-testid="import-desglose-preview-btn">
                    {loading ? <Loader2 size={14} className="animate-spin" /> : null}
                    {loading ? "Analizando..." : "Analizar archivo (vista previa)"}
                  </button>
                </div>
              )}
            </div>
          )}

          {preview && type === "bocas" && <BocasPreview preview={preview} />}
          {preview && type === "mano-obra" && <MoPreview preview={preview} />}
        </div>

        {preview && (
          <div className="px-6 pb-6 flex gap-3 sticky bottom-0 bg-white pt-2">
            <button onClick={reset} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">
              Elegir otro archivo
            </button>
            <button onClick={handleConfirm} disabled={confirming}
              className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60"
              data-testid="import-desglose-confirm-btn">
              {confirming ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
              {confirming ? "Importando..." : "Confirmar importación"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function StatBadge({ label, value, color }) {
  return (
    <div className={`rounded-lg px-3 py-2 text-center ${color}`}>
      <div className="text-lg font-bold tabular-nums">{value}</div>
      <div className="text-[10px] font-semibold uppercase tracking-wide opacity-80">{label}</div>
    </div>
  );
}

function BocasPreview({ preview }) {
  return (
    <div className="space-y-3" data-testid="bocas-desglose-preview">
      <div className="grid grid-cols-5 gap-2">
        <StatBadge label="Bocas" value={preview.n_bocas} color="bg-blue-50 text-blue-700" />
        <StatBadge label="Nuevas" value={preview.n_nuevas} color="bg-emerald-50 text-emerald-700" />
        <StatBadge label="A actualizar" value={preview.n_actualizar} color="bg-amber-50 text-amber-700" />
        <StatBadge label="Componentes" value={preview.n_componentes} color="bg-violet-50 text-violet-700" />
        <StatBadge label="Descartadas" value={preview.n_descartadas} color="bg-red-50 text-red-700" />
      </div>
      {preview.n_consolidadas > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
          <p className="font-bold flex items-center gap-1.5 mb-1"><AlertTriangle size={13} /> {preview.n_consolidadas} fila(s) duplicada(s) consolidadas (cantidades sumadas)</p>
          {preview.consolidadas.map((c, i) => (
            <p key={i} className="ml-4">• {c.descripcion} en "{c.boca}" — filas {c.filas.join(", ")} → cantidad total {c.cantidad_total}</p>
          ))}
        </div>
      )}
      {preview.n_descartadas > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800">
          <p className="font-bold flex items-center gap-1.5 mb-1"><AlertTriangle size={13} /> Filas no importadas</p>
          {preview.descartadas.map((d, i) => (
            <p key={i} className="ml-4">• Fila {d.fila}: {d.motivo}</p>
          ))}
        </div>
      )}
      <div className="border border-gray-200 rounded-xl overflow-hidden max-h-80 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 font-bold text-gray-500">Boca</th>
              <th className="text-center px-3 py-2 font-bold text-gray-500">Componentes</th>
              <th className="text-right px-3 py-2 font-bold text-gray-500">Precio calculado</th>
              <th className="text-right px-3 py-2 font-bold text-gray-500">Dif. vs Excel</th>
            </tr>
          </thead>
          <tbody>
            {preview.bocas.map((b, i) => (
              <tr key={i} className="border-b border-gray-50" data-testid={`bocas-preview-row-${i}`}>
                <td className="px-3 py-1.5">{b.tipo_boca}</td>
                <td className="px-3 py-1.5 text-center">{b.componentes.length}</td>
                <td className="px-3 py-1.5 text-right font-bold tabular-nums text-blue-700">{fmt(b.precio_mat_calculado)}</td>
                <td className={`px-3 py-1.5 text-right tabular-nums ${b.diff ? "text-red-600 font-bold" : "text-gray-300"}`}>
                  {b.diff ? `${b.diff > 0 ? "+" : ""}${fmt(b.diff)}` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MoPreview({ preview }) {
  return (
    <div className="space-y-3" data-testid="mo-desglose-preview">
      <div className="grid grid-cols-3 gap-2">
        <StatBadge label="Rubros" value={preview.n_rubros} color="bg-blue-50 text-blue-700" />
        <StatBadge label="Nuevos" value={preview.n_nuevos} color="bg-emerald-50 text-emerald-700" />
        <StatBadge label="A actualizar" value={preview.n_actualizar} color="bg-amber-50 text-amber-700" />
      </div>
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-600">
        Jornales usados: Oficial {fmt(preview.jornales_actuales.oficial_diario)}/día ({fmt(preview.costos_derivados.costo_hora_oficial)}/h) —
        Ayudante {fmt(preview.jornales_actuales.ayudante_diario)}/día ({fmt(preview.costos_derivados.costo_hora_ayudante)}/h)
      </div>
      <div className="border border-gray-200 rounded-xl overflow-hidden max-h-80 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 font-bold text-gray-500">Rubro</th>
              <th className="text-center px-3 py-2 font-bold text-gray-500">Hs Ay./Of.</th>
              <th className="text-right px-3 py-2 font-bold text-gray-500">Precio calculado</th>
              <th className="text-right px-3 py-2 font-bold text-gray-500">Dif. vs Excel</th>
            </tr>
          </thead>
          <tbody>
            {preview.rubros.map((r, i) => (
              <tr key={i} className="border-b border-gray-50" data-testid={`mo-preview-row-${i}`}>
                <td className="px-3 py-1.5">{r.descripcion} <span className="text-gray-400">({r.categoria})</span></td>
                <td className="px-3 py-1.5 text-center">{r.horas_ayudante}h / {r.horas_oficial}h</td>
                <td className="px-3 py-1.5 text-right font-bold tabular-nums text-emerald-700">{fmt(r.precio_calculado)}</td>
                <td className={`px-3 py-1.5 text-right tabular-nums ${r.diff ? "text-red-600 font-bold" : "text-gray-300"}`}>
                  {r.diff ? `${r.diff > 0 ? "+" : ""}${fmt(r.diff)}` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
